<template>
  <div>
    <!-- Ellipsis Menu -->
    <EllipsisMenu
      :actions="menuActions"
      menuIcon="fas fa-ellipsis-h fa-fw text-xl"
      buttonClass="p-2 text-gray-600 hover:text-gray-800"
    />

    <!-- Rename Watchlist Modal -->
    <TransitionRoot appear :show="isRenameModalOpen" as="template">
      <Dialog as="div" @close="closeRenameModal" class="relative z-50">
        <div class="fixed inset-0 bg-black/30" />
        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6">
              <DialogTitle class="text-lg font-medium mb-4">Rename Watchlist</DialogTitle>
              <div class="mb-5">
                <input
                  type="text"
                  v-model="newWatchlistName"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Watchlist name"
                  maxlength="50"
                  ref="nameInput"
                />
              </div>
              <div class="flex justify-end space-x-3">
                <button 
                  @click="closeRenameModal" 
                  class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  @click="saveWatchlistName"
                  :disabled="isRenaming || !newWatchlistName.trim()"
                  class="flex items-center px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i v-if="isRenaming" class="fas fa-spinner fa-spin mr-2"></i>
                  Save
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue';
import { store, updateWatchList, showDialog } from '../../store';
import EllipsisMenu from './EllipsisMenu.vue';
import { nextTick } from 'vue';
import * as Sentry from '@sentry/vue';
import { useRouter } from 'vue-router';

export default {
  name: 'MobileTableEllipsisMenu',
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    EllipsisMenu
  },
  emits: ['delete-watchlist'],
  setup() {
    const router = useRouter();
    return { router };
  },
  data() {
    return {
      store,
      isRenameModalOpen: false,
      isRenaming: false,
      newWatchlistName: ''
    };
  },
  computed: {
    menuActions() {
      return [
        {
          label: 'Rename Watchlist',
          icon: 'fas fa-edit',
          onClick: this.openRenameModal
        },
        {
          label: 'Delete Watchlist',
          icon: 'fas fa-trash',
          textColor: 'text-red-500',
          onClick: () => this.$emit('delete-watchlist')
        }
      ];
    }
  },
  methods: {
    openRenameModal() {
      if (!store.currentWatchList) return;
      
      this.newWatchlistName = store.currentWatchList.name;
      this.isRenameModalOpen = true;
      
      // Focus the name input after the modal is open
      nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
        }
      });
    },
    
    closeRenameModal() {
      this.isRenameModalOpen = false;
    },
    
    async saveWatchlistName() {
      if (!store.currentWatchListId) return;
      
      const newName = this.newWatchlistName.trim();
      if (newName === '' || newName === store.currentWatchList.name) {
        this.closeRenameModal();
        return;
      }
      
      this.isRenaming = true;
      try {
        const originalName = store.currentWatchList.name;
        store.currentWatchList.name = newName;
        
        const success = await updateWatchList(store.currentWatchListId, {
          name: newName
        });
        
        if (success) {
          store.tableState.name = newName;
          showDialog({
            message: 'Watchlist renamed successfully',
            type: 'success',
            timeout: 3000
          });
        } else {
          throw new Error('Failed to update watchlist name');
        }
      } catch (error) {
        Sentry.setExtra('method', 'saveWatchlistName');
        Sentry.captureException(error);
        console.error("Error saving watchlist name:", error);
        
        // Revert the name if there was an error
        store.currentWatchList.name = this.newWatchlistName;
        
        showDialog({
          message: 'Failed to rename watchlist',
          type: 'error',
          timeout: 3000
        });
      } finally {
        this.isRenaming = false;
        this.closeRenameModal();
      }
    }
  }
};
</script> 